import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { navItems, findMeLinks } from '../data/mockData';
import { LiveClock, StatusIndicator } from './LiveClock';

// Header shown on mobile/tablet. Includes hamburger that opens a Sheet with full sidebar contents.
export default function MobileHeader() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const pageTitle = (() => {
    const item = navItems.find(n => n.path === location.pathname);
    if (item) return item.label;
    if (location.pathname.startsWith('/work/')) return 'Case Study';
    if (location.pathname === '/onboarding') return 'Sign the guestbook';
    return 'Manisha\u2019s World';
  })();

  return (
    <header className="lg:hidden sticky top-0 z-40 bg-cream-100/95 backdrop-blur-sm border-b border-cream-300/50">
      <div className="flex items-center justify-between px-4 py-3">
        <Link to="/home" className="flex items-center gap-2">
          <svg width="28" height="28" viewBox="0 0 42 42">
            <path d="M21 6 L23 14 L31 16 L23 18 L21 26 L19 18 L11 16 L19 14 Z" fill="#e8a55a" />
            <circle cx="33" cy="10" r="2" fill="#7eb8b8" />
            <circle cx="10" cy="30" r="2.5" fill="#e8a5a8" />
          </svg>
          <span className=" text-lg text-inkwell-900">Manisha Pundir</span>
        </Link>
        <div className="flex items-center gap-3">
          <span className="hidden sm:block text-[10px] uppercase tracking-wider text-inkwell-500">{pageTitle}</span>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className="relative flex items-center justify-center w-10 h-10 rounded-xl border border-cream-300/70 bg-cream-50/80 text-inkwell-900 shadow-sm transition-all duration-200 hover:bg-cream-200 hover:border-cream-400 hover:shadow active:scale-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-inkwell-900/20"
                aria-label={open ? 'Close menu' : 'Open menu'}
                aria-expanded={open}
              >
                <Menu
                  className={`w-5 h-5 transition-all duration-300 ${open ? 'opacity-0 rotate-90 scale-50' : 'opacity-100 rotate-0 scale-100'}`}
                />
                <X
                  className={`w-5 h-5 absolute transition-all duration-300 ${open ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-50'}`}
                />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[340px] bg-cream-100 border-cream-300/60 p-0 overflow-y-auto">
              <div className="px-6 py-7">
                <div className="flex items-center justify-between mb-5">
                  <Link to="/" onClick={() => setOpen(false)}>
                    <svg width="38" height="38" viewBox="0 0 42 42">
                      <path d="M21 6 L23 14 L31 16 L23 18 L21 26 L19 18 L11 16 L19 14 Z" fill="#e8a55a" />
                      <circle cx="33" cy="10" r="2" fill="#7eb8b8" />
                      <circle cx="10" cy="30" r="2.5" fill="#e8a5a8" />
                      <rect x="30" y="28" width="3" height="3" fill="#5a7faf" transform="rotate(45 31.5 29.5)" />
                    </svg>
                  </Link>
                </div>

                <h1 className=" text-[30px] leading-none text-inkwell-900">Manisha Pundir</h1>
                <p className=" text-[10px] uppercase tracking-wider text-inkwell-500 mt-2">Product Design Lead @ RhythmQ</p>
                <p className="text-[13px] text-inkwell-700 mt-4 leading-relaxed">
                   I am a designer who loves building delightful software that solves real problems.
                </p>

                <div className="mt-6 bg-cream-200/60 rounded-md p-2 border border-cream-300/60">
                  <p className=" text-[10px] uppercase tracking-wider text-inkwell-500 px-2 pt-1 pb-2">Explore</p>
                  <nav className="flex flex-col">
                    {navItems.map((item) => {
                      const active = location.pathname === item.path;
                      return (
                        <Link
                          key={item.no}
                          to={item.path}
                          onClick={() => setOpen(false)}
                          className={`flex items-center justify-between px-2 py-2 rounded text-[13px] transition-colors ${active ? 'bg-cream-100 text-inkwell-900' : 'text-inkwell-700 hover:bg-cream-100/60'}`}
                        >
                          <span className="flex items-center gap-2">
                            <span className="text-inkwell-500">{item.no}.</span>
                            <span>{item.label}</span>
                            {item.badge && <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: 'var(--visitor-accent)' }} />}
                          </span>
                          <span>→</span>
                        </Link>
                      );
                    })}
                  </nav>
                </div>

                <div className="mt-6">
                  <p className=" text-[10px] uppercase tracking-wider text-inkwell-500">Find Me At</p>
                  <p className="text-[13px] text-inkwell-700 mt-2">
                    {findMeLinks.map((l, i) => (
                      <React.Fragment key={l.label}>
                        <a href={l.href} target="_blank" rel="noopener noreferrer" className="link-underline">{l.label}</a>
                        {i < findMeLinks.length - 1 && ', '}
                      </React.Fragment>
                    ))}
                  </p>
                </div>

                <div className="mt-10">
                  <LiveClock className="text-[11px] text-inkwell-700 block" />
                  <StatusIndicator className="mt-1" />
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
